import { useEffect, useMemo, useRef } from "react";
import { useQuery } from "@apollo/client";

import DetailsCardThreeColumnRow from "@/components/DetailsCardThreeColumnRow";
import useCapabilities, {
  DOMAIN,
  PERMISSION,
  useCanManageCareerPlanBool,
  useCanViewCareerPlanBool,
  useRequireCapability
} from "@/hooks/useCapabilities";
import useAmplitude from "@/hooks/useAmplitude";
import { useFlags, useFlagsLoaded } from "@/hooks/useFeatureFlags";
import { Navigate, useParams } from "@/router";
import { GET_CAREER_PLAN_HUB_QUERY } from "../queries";
import { CAREER_PLAN_ANALYTICS_EVENTS } from "./amplitude";
import CareerPlanCard from "./_CareerPlanCard";
import CurrentCareerGoalsCard from "./_CurrentCareerGoalsCard";
import ExperienceIndexSummaryCard from "./_ExperienceIndexSummaryCard";
import { getExperienceIndexCompletion, getExperienceIndexHubState } from "./careerPlanHubSummaries";
import { getCurrentCareerIntent } from "./careerPlanHistory";

export default function CareerPlanHub() {
  useRequireCapability({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.viewCareerPlan
  });
  const { pgaId } = useParams("/people/:pgaId/career/career-profile");
  const { trackEvent } = useAmplitude();
  const flags = useFlags();
  const flagsLoaded = useFlagsLoaded();
  const { loaded: capabilitiesLoaded } = useCapabilities();
  const canViewCareerPlan = useCanViewCareerPlanBool();
  const canManageCareerPlan = useCanManageCareerPlanBool();
  const careerTargetsEnabled = flags["pga-career-targets"] === true;
  const careerPlansEnabled = flags["pga-career-plan"] === true;
  const trackedLoadKey = useRef<string | null>(null);

  const { data, loading, error } = useQuery(GET_CAREER_PLAN_HUB_QUERY, {
    variables: { pgaId: pgaId || "" },
    skip: !pgaId || !flagsLoaded || !capabilitiesLoaded || !careerTargetsEnabled || !canViewCareerPlan
  });

  const currentGoal = useMemo(
    () => getCurrentCareerIntent(data?.person?.careerIntent, data?.person?.careerIntents),
    [data?.person?.careerIntent, data?.person?.careerIntents]
  );
  const assessment = data?.person?.experienceIndexAssessment ?? null;

  useEffect(() => {
    if (loading || error || !data?.person || !pgaId) return;
    const key = `${pgaId}:${data.person.id}`;
    if (trackedLoadKey.current === key) return;
    trackedLoadKey.current = key;

    const completion = getExperienceIndexCompletion(assessment);
    trackEvent(CAREER_PLAN_ANALYTICS_EVENTS.hubViewed, {
      pgaId,
      hasCurrentCareerGoals: !!currentGoal,
      canEdit: canManageCareerPlan,
      source: "career_plan_hub",
      answeredCount: completion.answered,
      questionCount: completion.total,
      hasExperienceIndexResults: getExperienceIndexHubState(completion) === "finished"
    });
  }, [assessment, canManageCareerPlan, currentGoal, data?.person, error, loading, pgaId, trackEvent]);

  if (!flagsLoaded || !capabilitiesLoaded) return null;
  if (!careerTargetsEnabled && pgaId) {
    return <Navigate to="/people/:pgaId/career/employment" params={{ pgaId }} replace />;
  }

  if (error) {
    return <div>Error: {error.message}</div>;
  }

  return (
    <div className="pt-12" data-testid="career-plan-hub">
      <DetailsCardThreeColumnRow>
        <CurrentCareerGoalsCard
          goal={currentGoal}
          loading={loading}
          pgaId={pgaId}
          hasArchivedCareerGoals={(data?.person?.careerIntents ?? []).some((goal) => goal.archivedAt != null)}
          canManageCareerPlan={canManageCareerPlan}
        />
        <ExperienceIndexSummaryCard
          assessment={assessment}
          loading={loading}
          pgaId={pgaId}
          canManageCareerPlan={canManageCareerPlan}
        />
        <CareerPlanCard
          loading={loading}
          pgaId={pgaId}
          plans={data?.person?.careerPlans ?? []}
          canCreatePlan={careerPlansEnabled && canManageCareerPlan}
        />
      </DetailsCardThreeColumnRow>
    </div>
  );
}
